import { useEffect } from "react"

import { type AiGenerationType, aiGenerationTypes } from "@forento/core/models/ai"

import routes from "~/utilities/routes"

// The adoption funnel is banner/card → modal → generation, and the revenue funnel is out of credits →
// credit packs → checkout. The claim and the completed purchase are captured by the API.
type ForentoAiEvent =
	| "forento_ai_banner_viewed"
	| "forento_ai_banner_claim_clicked"
	| "forento_ai_card_viewed"
	| "forento_ai_card_clicked"
	| "forento_ai_modal_opened"
	| "forento_ai_out_of_credits_viewed"
	| "forento_ai_generation_submitted"
	| "forento_ai_generation_succeeded"
	| "forento_ai_generation_failed"
	| "forento_ai_credit_packs_viewed"
	| "forento_ai_credit_checkout_started"

type Properties = Record<string, string | number | boolean | null>

export function captureForentoAiEvent(event: ForentoAiEvent, properties?: Properties): void {
	window.posthog?.capture(event, properties)
}

// Properties are read when the element becomes visible, so later changes do not capture it again.
export function useForentoAiImpression(event: ForentoAiEvent, properties: Properties, isVisible = true): void {
	useEffect(() => {
		if (isVisible) captureForentoAiEvent(event, properties)
	}, [isVisible])
}

export function useForentoAiModalTracking(args: {
	type: AiGenerationType
	isFormVisible: boolean
	creditBalance: number
	cost: number
}): void {
	const hasEnoughCredits = args.creditBalance >= args.cost
	useForentoAiImpression(
		"forento_ai_modal_opened",
		{ type: args.type, has_enough_credits: hasEnoughCredits },
		args.isFormVisible,
	)
	useForentoAiImpression(
		"forento_ai_out_of_credits_viewed",
		{ type: args.type, credit_balance: args.creditBalance, cost: args.cost },
		args.isFormVisible && !hasEnoughCredits,
	)
}

const creditSourceParam = "ai-credits-source"

// Attributes the credit packs view and checkout to the generation that sent the creator there.
export function getBuyAiCreditsUrl(source: AiGenerationType): string {
	return `${routes.settings.billing.plans()}?${new URLSearchParams({ [creditSourceParam]: source })}`
}

export function getBuyAiCreditsSource(searchParams: URLSearchParams): AiGenerationType | null {
	const source = searchParams.get(creditSourceParam)
	return aiGenerationTypes.find(type => type === source) ?? null
}
